import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import * as kameraStrom from './kameraStrom'

/** Lange Kante des übertragenen Fotos in Pixeln. */
const MAX_KANTE = 1600

/** Zwei Tipps gelten als Doppeltipp, wenn sie so kurz nacheinander und so nah beieinander liegen. */
const DOPPELTIPP_MS = 350
const DOPPELTIPP_ABSTAND_PX = 40

interface Eigenschaften {
  /** Wird mit dem aufgenommenen Foto aufgerufen. */
  beiAufnahme: (bild: Blob) => void
  /** Sperrt den Ausloeser, solange das vorige Bild noch hochgeladen wird. */
  gesperrt?: boolean
  hinweis?: string
}

/**
 * Live-Vorschau der Rueckkamera mit Ausloeser, Rasterhilfe und - sofern das Geraet es
 * unterstuetzt - Taschenlampe fuer dunkle Kellerlager.
 *
 * <p>Das Videoelement gehoert nicht dieser Komponente, sondern `kameraStrom`; hier wird es nur in
 * den Rahmen gehaengt. So bleibt der Sucher ueber Bildschirmwechsel hinweg derselbe und Safari
 * fragt nur einmal um Erlaubnis.
 *
 * <p>Wichtig fuer den Betrieb: getUserMedia funktioniert nur auf https-Seiten oder auf
 * localhost. Auf dem Smartphone muss die Anwendung also ueber die Traefik-Adresse
 * aufgerufen werden, nicht ueber die IP des Entwicklungsrechners.
 */
export default function Kamera({ beiAufnahme, gesperrt, hinweis }: Eigenschaften) {
  const behaelterRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [blitzMoeglich, setBlitzMoeglich] = useState(false)
  const [bestaetigt, setBestaetigt] = useState(false)

  // Die Leuchte gehört dem Strom, nicht diesem Bildschirm - sie bleibt beim Wechsel an.
  const blitzAn = useSyncExternalStore(kameraStrom.blitzAbonnieren, kameraStrom.blitzZustand)

  useEffect(() => {
    let abgebrochen = false
    let wachhund: number | undefined

    // Der Sucher wird vorne eingehaengt, damit Raster und Bestaetigung darueber liegen.
    const einhaengen = (video: HTMLVideoElement) => {
      videoRef.current = video
      behaelterRef.current?.prepend(video)
      setBlitzMoeglich(kameraStrom.blitzMoeglich())
    }

    kameraStrom
      .anfordern()
      .then((video) => {
        if (abgebrochen) return
        einhaengen(video)

        // Bleibt das Bild trotz laufendem Strom schwarz, wird einmal neu geholt. Das faengt die
        // Faelle ab, in denen iOS den Sucher nach einem Bildschirmwechsel nicht wieder anwirft.
        wachhund = window.setTimeout(() => {
          if (abgebrochen || kameraStrom.liefertBild()) return
          kameraStrom
            .neustarten()
            .then((erneuert) => {
              if (!abgebrochen) einhaengen(erneuert)
            })
            .catch(() => setFehler('Der Sucher liefert kein Bild. Bitte die Seite neu laden.'))
        }, kameraStrom.wachhundFrist)
      })
      .catch(() => {
        if (abgebrochen) return
        setFehler(
          'Kein Kamerazugriff. Bitte die Berechtigung erlauben und sicherstellen, dass die Seite über https aufgerufen wird.',
        )
      })

    // Genau ein freigeben() je anfordern(), auch wenn der Strom erst nach dem Verlassen eintrifft.
    return () => {
      abgebrochen = true
      window.clearTimeout(wachhund)
      videoRef.current = null
      kameraStrom.freigeben()
    }
  }, [])

  const blitzUmschalten = useCallback(async () => {
    if (!(await kameraStrom.blitzSetzen(!blitzAn))) {
      setBlitzMoeglich(false)
    }
  }, [blitzAn])

  const ausloesen = useCallback(() => {
    const video = videoRef.current
    if (!video || video.videoWidth === 0) return

    // Verkleinert auf die lange Kante. Drei Gründe: die Übertragung über eine Mobil- oder
    // VPN-Strecke dauert sonst spürbar länger, die Vorverarbeitung der Erkennungsdienste ist
    // laut deren Dokumentation vom JPEG-Dekodieren begrenzt (Verkleinern ist dort die
    // wirksamste Beschleunigung), und die Modelle arbeiten ohnehin mit 640 bzw. 320 Pixeln.
    // 1600 Pixel lassen Etiketten für den Menschen noch lesbar.
    const massstab = Math.min(1, MAX_KANTE / Math.max(video.videoWidth, video.videoHeight))
    const breite = Math.round(video.videoWidth * massstab)
    const hoehe = Math.round(video.videoHeight * massstab)

    const flaeche = document.createElement('canvas')
    flaeche.width = breite
    flaeche.height = hoehe
    const kontext = flaeche.getContext('2d')
    if (!kontext) return
    kontext.drawImage(video, 0, 0, breite, hoehe)

    flaeche.toBlob(
      (bild) => {
        if (!bild) return
        // Sofortige Rückmeldung: der Haken erscheint, noch bevor der Server geantwortet hat.
        setBestaetigt(true)
        window.setTimeout(() => setBestaetigt(false), 700)
        beiAufnahme(bild)
      },
      'image/jpeg',
      0.85,
    )
  }, [beiAufnahme])

  // Doppeltipp auf den Sucher loest aus: vor dem Regal ist der Daumen oft naeher am Bild als am
  // Ausloeser. Erkannt wird er von Hand, nicht ueber dblclick - iOS nutzt den Doppeltipp sonst zum
  // Zoomen, und dblclick kommt auf Touchgeraeten nicht verlaesslich an.
  const letzterTipp = useRef<{ zeit: number; x: number; y: number } | null>(null)
  const tippImSucher = useCallback(
    (ereignis: React.PointerEvent<HTMLDivElement>) => {
      // Die Lichttaste im Sucher bleibt eine eigene Schaltflaeche.
      if ((ereignis.target as HTMLElement).closest('button')) return
      if (gesperrt || fehler) return

      const jetzt = ereignis.timeStamp
      const vorher = letzterTipp.current
      const nahGenug =
        vorher !== null &&
        Math.hypot(ereignis.clientX - vorher.x, ereignis.clientY - vorher.y) < DOPPELTIPP_ABSTAND_PX

      if (vorher !== null && jetzt - vorher.zeit < DOPPELTIPP_MS && nahGenug) {
        letzterTipp.current = null
        ausloesen()
        return
      }
      letzterTipp.current = { zeit: jetzt, x: ereignis.clientX, y: ereignis.clientY }
    },
    [ausloesen, gesperrt, fehler],
  )

  return (
    <div className="gruppe">
      <div className="kamera" ref={behaelterRef} onPointerUp={tippImSucher}>
        {fehler ? (
          <p className="hinweis">{fehler}</p>
        ) : (
          <>
            <div className="raster" />
            {blitzMoeglich && (
              <button className="blitz" onClick={blitzUmschalten} aria-pressed={blitzAn}>
                {blitzAn ? 'Licht aus' : 'Licht an'}
              </button>
            )}
            {bestaetigt && (
              <div className="aufnahme-bestaetigt" role="status" aria-label="Foto aufgenommen">
                <svg viewBox="0 0 48 48" aria-hidden="true">
                  <circle cx="24" cy="24" r="21" />
                  <path d="M14 25 L21 32 L34 17" />
                </svg>
              </div>
            )}
          </>
        )}
      </div>

      {hinweis && <p className="sek">{hinweis}</p>}

      <button
        className="ausloeser"
        onClick={ausloesen}
        disabled={gesperrt || Boolean(fehler)}
        aria-label="Foto aufnehmen"
      >
        <i />
      </button>
    </div>
  )
}
